import { padronesEnum } from 'views/Tramites/GPM/enums';
import getAddress from 'utils/address';
import { store } from 'store';
import { listToString, normalizeText } from 'utils/normalizers';

const PROD = process.env.NODE_ENV === 'production';
const IPROVINAY = process.env.REACT_APP_ENV_NAME === 'iprovinay';
const noKey = (padron) => (PROD ? 'Sin identificador' : `ID: ${padron.id}`);
const vehiculares = [padronesEnum.VEHICULO, padronesEnum.MOTOCICLETA, padronesEnum.REMOLQUE];

const getKeyPredio = (predio = {}) => {
  const config = store.getState().catalogs.llavesDePredios?.find((e) => e.es_principal);
  const keysMap = {
    1: 'cuenta_unica_de_predial',
    2: 'CURT',
    3: 'clave_catastral_estandar',
    4: 'clave_catastral_municipal',
    5: 'clave_sectorial',
  };

  if (config) {
    const key = keysMap[config.clave_de_predio];
    if (IPROVINAY) {
      return predio?.clave || predio[key] || noKey(predio, normalizeText(key));
    }
    return predio[key] || noKey(predio, normalizeText(key));
  }

  const keyWithValue = Object.values(keysMap).find((k) => predio[k]);
  return keyWithValue ? predio[keyWithValue] : noKey(predio);
};

const _getPadronKey = (padron, tipoDePadron) => {
  if ('empresa' in padron) {
    return padron.clave || padron?.empresa?.clave;
  }
  if (vehiculares.includes(tipoDePadron)) {
    return padron.numero_de_placa_vigente || padron.placa || padron.serie
      || padron.numero_de_identificacion_vehicular;
  }
  if (tipoDePadron === padronesEnum.PREDIO) return getKeyPredio(padron);
  if (tipoDePadron === padronesEnum.CONTRIBUYENTE && padron.es_autoridad_externa) return '';
  return padron.clave_ciudadana || padron.clave;
};

export const getPadronKey = (pd, tipoDePadron) => _getPadronKey(pd, tipoDePadron) || noKey(pd);

export const getNombreCompleto = (padron) => {
  if (padron.razon_social) return padron.razon_social;
  if (padron.nombre_completo_corto) return padron.nombre_completo_corto;
  if (padron.nombre_completo) return padron.nombre_completo;
  const nombre_completo = ['nombre', 'apellido_paterno', 'apellido_materno']
    .map((f) => padron[f] || '').join(' ').trim();
  if (nombre_completo) return nombre_completo;
  const fields = ['contribuyente', 'contribuyente_propietario', 'ciudadano', 'propietario'];
  const field = fields.find((f) => padron[f] && typeof padron[f] === 'object');
  if (field) return getNombreCompleto(padron[field]);
  if (!field) return 'Sin información';

  return '';
};

const fieldToCensure = (field) => /descripcion_predio/.test(field);

const map = (padron, descriptors) => descriptors.map((descriptor) => {
  if (Array.isArray(descriptor)) {
    const [_padronKey, ..._descriptors] = descriptor;
    return padron[_padronKey] ? map(padron[_padronKey], _descriptors) : null;
  }
  if (typeof descriptor === 'function') {
    return descriptor(padron);
  }
  if (descriptor === 'numero_de_placa_vigente' && !padron[descriptor]) return 'Sin placa';
  return fieldToCensure(descriptor) && padron.censure
    ? (padron[descriptor] || '').split(' ').map((e) => e.replace(/(?<!^)./g, '*')).join(' ')
    : padron[descriptor];
});

const join = (padron, descriptors) => map(padron, descriptors).flat(Infinity).filter((e) => e).join(' - ');

const getAddressPredio = (p) => getAddress(p.direccion);
const getAddressLicencia = (e) => getAddress(e.direccion_de_ubicacion);

const getPropietarioPredio = (padron) => {
  if (padron.nombres_de_participes?.length) return listToString(padron.nombres_de_participes);
  if (padron.ciudadano && typeof padron.ciudadano === 'object') return getNombreCompleto(padron.ciudadano);
  if (padron.contribuyente && typeof padron.contribuyente === 'object') return getNombreCompleto(padron.contribuyente);
  return '';
};

export const predioToString = (padron, withAddress = true) => join(
  padron,
  [getKeyPredio, getPropietarioPredio, withAddress && getAddressPredio].filter((e) => e),
);

export const ciudadanoToString = (p) => join(p, ['clave_ciudadana', getNombreCompleto]);
export const contribuyenteToString = (p) => join(p, ['clave', getNombreCompleto]);
export const propietarioToString = (p) => {
  const field = Object.keys(p).find((f) => /contribuyente|ciudadano/.test(f) && p[f]);
  if (!field) return 'Sin información';
  return (/contribuyente/.test(field) ? contribuyenteToString : ciudadanoToString)(p[field]);
};
export const empresaToString = (p) => join(p, ['clave', 'nombre_comercial']);
export const vehiculoToString = (p) => join(p, ['numero_de_placa_vigente', getNombreCompleto]);
export const motoToString = (p) => join(p, ['numero_de_placa_vigente', 'serie']);
export const remolqueToString = (p) => join(p, ['placa', 'serie']);
export const mercadosToString = (p) => join(p, ['clave', getNombreCompleto]);
export const restoDePadronesToString = (p) => join(p, ['clave', 'descripcion', ['empresa', 'nombre_comercial']]);
export const licenciaFuncionamientoToString = (p) => join(p, ['clave_de_control', ['empresa', getNombreCompleto], ['empresa', empresaToString], ['empresa', getAddressLicencia]]);
export const infraccionesToString = (p) => join(p, ['clave', getNombreCompleto, 'numero_de_placa']);
export const panteonToString = (p) => {
  const {
    folio,
    contribuyente,
    contributente,
  } = p;

  const _contribuyente = contribuyente || contributente;

  const nombreContribuyente = _contribuyente ? contribuyenteToString(_contribuyente) : '';

  const output = [p?.clave || folio || 'Sin clave', nombreContribuyente || 'Sin nombre'];
  return output.filter((e) => e).join(' - ');
};
export const moduloToString = (p) => {
  const {
    contribuyente,
    contributente,
  } = p;

  const _contribuyente = contribuyente || contributente;

  const nombreContribuyente = _contribuyente ? contribuyenteToString(_contribuyente) : '';

  const output = [p?.clave || 'Sin clave', nombreContribuyente || 'Sin nombre'];
  return output.filter((e) => e).join(' - ');
};

const padronToString = (_padron, tipo_de_padron, withTipoDePadron = false, withAddress = true) => {
  if (!_padron) {
    return '';
  }

  const padron = { ..._padron };
  const { padrones } = store.getState().catalogs;
  const tipoDePadronStr = withTipoDePadron
    ? padrones.find((e) => e.id === tipo_de_padron)?.descripcion.toUpperCase().concat(':') : '';
  if ('nombre_completo' in padron) {
    padron.nombre_completo = padron.nombre_comercial || padron.nombre_completo_corto
      || padron.nombre_completo || getNombreCompleto(padron);
    if (padron.nombre_completo?.includes(`${padron.clave_ciudadana || padron.clave} -`)) {
      [, padron.nombre_completo] = padron.nombre_completo
        .split(padron.clave || padron.clave_ciudadana);
    }
  }
  const padronToStringMap = {
    [padronesEnum.CIUDADANO]: ciudadanoToString,
    [padronesEnum.EMPRESA]: empresaToString,
    [padronesEnum.PREDIO]: (p) => predioToString(p, withAddress),
    [padronesEnum.VEHICULO]: vehiculoToString,
    [padronesEnum.CONTRIBUYENTE]: contribuyenteToString,
    [padronesEnum.MOTOCICLETA]: motoToString,
    [padronesEnum.REMOLQUE]: remolqueToString,
    [padronesEnum.INFRACCIONES]: infraccionesToString,
    [padronesEnum.MULTAS]: infraccionesToString,
    [padronesEnum.MERCADO]: mercadosToString,
    [padronesEnum.EXPEDIENTE_LICENCIA_FUNCIONAMIENTO]: licenciaFuncionamientoToString,
    [padronesEnum.LOTES_DE_PANTEON]: panteonToString,
    [padronesEnum.COMERCIO_INFORMAL]: mercadosToString,
    [padronesEnum.MODULOS]: moduloToString,
  };
  if (padronToStringMap[tipo_de_padron]) {
    return `${tipoDePadronStr} ${padronToStringMap[tipo_de_padron](padron)}`.trim() || 'Sin información';
  }

  // resto de padrones
  const str = restoDePadronesToString(padron);
  return str ? `${tipoDePadronStr} ${str}`.trim() : 'Sin información';
};

export default padronToString;
